/**
 * CMS Component Renderer
 *
 * A component that dynamically renders CMS content based on component type.
 * Handles feature flag integration, template resolution, and loading states.
 */

'use client';

import {
    CMSComponentListProps,
    CMSComponentRendererProps,
    isBanner,
    isCard,
    isSection,
    isTitle,
    processBannerTemplates,
    processCardTemplates,
    processTitleTemplates,
} from '@/@cms/types';
import dynamic from 'next/dynamic';
import React from 'react';

// Dynamically import CMS components to reduce initial bundle size
// This improves initial page load performance by code-splitting
const BannerComponent = dynamic(() => import('./Home/Banner'));
const CardComponent = dynamic(() => import('./Home/Card'));
const TitleComponent = dynamic(() => import('./Home/Title'));
const SectionComponent = dynamic(() => import('./Home/Section'));

export const CMSComponentRenderer = ({
    component,
    additionalProps = {},
    templateValues = {},
}: CMSComponentRendererProps) => {
    // Early return if no component data
    if (!component) return null;

    // Determine the component type and render accordingly
    if (isBanner(component)) {
        // Process banner template variables
        const processedBanner = processBannerTemplates(component, templateValues);

        return <BannerComponent banner={processedBanner} {...additionalProps} />;
    }

    if (isTitle(component)) {
        // Process title template variables
        const processedTitle = processTitleTemplates(component, templateValues);

        return <TitleComponent data={processedTitle} {...additionalProps} />;
    }

    // Handle sections (which contain cards)
    if (isSection(component)) {
        return <SectionComponent data={component} {...additionalProps} />;
    }

    // Handle individual cards
    if (isCard(component)) {
        // Process card template variables
        const processedCard = processCardTemplates(component, templateValues);

        return <CardComponent data={processedCard} {...additionalProps} />;
    }

    // Unknown component type - log warning and return null
    if (process.env.NODE_ENV !== 'production') {
        console.warn('Unknown CMS component type:', component);
    }

    return null;
};

/**
 * Component that renders a list of CMS components
 *
 * @param props - Component props
 * @returns Rendered list of CMS components
 *
 * @example
 * ```tsx
 * <CMSComponentList
 *   components={headerComponents}
 *   templateValues={{ 'user.name': 'John' }}
 * />
 * ```
 */
export const CMSComponentList: React.FC<CMSComponentListProps> = ({
    components,
    additionalProps = {},
    templateValues = {},
}) => {
    // Handle empty component list
    if (!components || !Array.isArray(components) || components.length === 0) {
        return null;
    }

    return (
        <>
            {components.map((component, index) => (
                <CMSComponentRenderer
                    key={component.id || `component-${index}`}
                    component={component}
                    additionalProps={additionalProps}
                    templateValues={templateValues}
                />
            ))}
        </>
    );
};

// Default export for dynamic imports
export default CMSComponentRenderer;
